import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Hero() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (name: string) => {
    const cat = categories.find((c) => c.name === name);
    if (cat) {
      window.open(cat.url, '_blank');
    }
    setSelectedCategory('');
    setDropdownOpen(false);
  };

  return (
    <section className="relative w-full min-h-[580px] md:min-h-[700px] flex items-center justify-center overflow-hidden pt-16">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            'url(https://readdy.ai/api/search-image?query=Soft%20airy%20abstract%20background%20with%20gentle%20organic%20flowing%20shapes%20in%20warm%20ivory%20cream%20and%20muted%20sage%20green%20tones%2C%20subtle%20product%20lifestyle%20aesthetic%2C%20clean%20editorial%20composition%20with%20delicate%20layered%20textures%2C%20light%20ethereal%20atmosphere%2C%20modern%20minimal%20gradient%20overlay%2C%20high%20detail%20harmonious%20palette%2C%20warm%20sophisticated%20brightness&width=1600&height=900&seq=hero-bg-vb-v2&orientation=landscape)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/8 to-black/20"></div>

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 py-16 md:py-24 text-center">
        <div className="max-w-3xl mx-auto">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100/80 text-accent-800 text-xs font-semibold tracking-wide uppercase mb-6">
            Updated for 2026
          </span>

          <h1 className="font-heading font-extrabold text-3xl md:text-5xl lg:text-6xl text-white leading-tight mb-5">
            VigorBuy Spreadsheet 2026
          </h1>

          <p className="text-lg md:text-xl text-white/90 font-light max-w-2xl mx-auto mb-10 leading-relaxed">
            Find Products Faster With Organized Shopping Lists — discover updated resources, product categories,
            comparison guides and expert tips designed to make online product research easier, faster and safer.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white text-foreground-900 rounded-full text-base font-semibold hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-black/10"
            >
              VigorBuy Spreadsheet
              <i className="ri-arrow-right-up-line text-lg"></i>
            </a>

            <div className="relative w-full sm:w-auto" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-accent-500 text-white rounded-full text-base font-semibold hover:bg-accent-600 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-accent-500/25"
              >
                Browse Categories
                <i className={`${dropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg transition-transform`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 sm:left-1/2 sm:-translate-x-1/2 w-full sm:w-64 bg-white rounded-2xl shadow-2xl border border-background-200 overflow-hidden z-50 max-h-[420px] overflow-y-auto">
                  <div className="p-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleCategorySelect(cat.name)}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors">
                          <i className={`${cat.icon} text-primary-600 text-sm`}></i>
                        </div>
                        <span className="font-medium whitespace-nowrap">{cat.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6">
            {['Updated Resources', 'Product Guides', 'Easy Comparison', 'Safety Tips'].map((item) => (
              <div key={item} className="flex items-center gap-2 text-white/80 text-sm">
                <i className="ri-checkbox-circle-fill text-accent-400 text-base"></i>
                <span className="whitespace-nowrap">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}